// web_components/wcContent/wcContent.ts

import { WebComponent_Base } from '../WebComponent_Base';
import { WebComponent_Utils } from '../WebComponent_Utils';
import { handleFormSubmission, WcContent_Utils } from './wcContent_Utils';
import { renderProductHTML } from './wcContent_Renderer';
import { NotificationService, NotificationType } from '../../utils/NotificationService';

/**
 * A custom web component for displaying content alongside an image.
 * This component allows optionally displaying a title, subtitle, text block, and CTA button.
 * The content and image sections can be positioned based on a data attribute.
 */
export class WcContent extends WebComponent_Base {
	public dataAttributes: any;
	public instanceId: string;

	constructor() {
		super();
		this.dataAttributes = {};
		this.instanceId = WebComponent_Utils.generateUniqueId();
	}

	/**
	 * Gets the name of the component.
	 *
	 * This getter overrides the base class implementation to return the name
	 * of the component. This value is used for logging and
	 * identifying the component in error messages or other diagnostics.
	 *
	 * @returns {string} The name of the component`.
	 */
	protected get componentName(): string {
		return 'WcContent';
	}

	/**
	 * Retrieves data attributes.
	 * Called from the BaseComponent after stylesheets are attached.
	 */
	protected getComponentData(): void {
		this.getData();
	}

	/**
	 * Lifecycle hook called when the component is connected to the DOM.
	 */
	protected onConnected(): void {
		// Add aria-hidden to all child nodes of the light DOM to hide fallback content
		// from screen readers
		Array.from(this.children).forEach((child) => {
			child.setAttribute('aria-hidden', 'true');
		});
		// Notify utils that this component is ready
		WcContent_Utils.registerInstance(this);
	}

	/**
	 * Lifecycle hook called when the component is removed from the DOM.
	 */
	protected onDisconnected(): void {
		// Any cleanup logic specific to WcContent can be added here
		WcContent_Utils.unregisterInstance(this);
	}

	/**
	 * Transfers data attributes from the light DOM elements to this.dataAttributes.
	 * This includes data-css for styling, store ids, product type, variables etc.
	 */
	private getData(): any {
		const jsonLdScript = this.querySelector('script[type="application/ld+json"]');
		if (jsonLdScript) {
			try {
				this.dataAttributes['variables'] = JSON.parse(<string>jsonLdScript.textContent);
			} catch (e) {
				console.error('wcContent Component: Invalid JSON-LD:', e);
				this.dataAttributes['variables'] = null;
			}
		}

		// CSS
		const css = this.getAttribute('data-css');
		if (css) {
			try {
				this.dataAttributes['css'] = JSON.parse(css);
			} catch (e) {
				console.error('wcContent Component: Invalid CSS in data-css attribute:', e);
				this.dataAttributes['css'] = null;
			}
		}
	}

	/**
	 * Renders the component based on the provided `dataAttributes` and `instanceId`.
	 * Appends the generated product content directly to the shadow DOM, after the attached styles.
	 *
	 * @protected
	 * @returns {HTMLElement | null} A reference to the first child of the appended content in the shadow DOM,
	 *                               or `null` if no content was appended.
	 */
	protected render(): HTMLElement | null {
		// Generate the content as a string
		const renderedHTML: string = renderProductHTML(this.dataAttributes, this.instanceId);

		// Convert the string to a DOM node
		const template = document.createElement('template');
		template.innerHTML = renderedHTML;

		// Append the content from the template to the shadow DOM
		this.shadowRoot!.appendChild(template.content.cloneNode(true));

		// Return the reference to the first child of the appended content
		return this.shadowRoot!.lastElementChild as HTMLElement | null;
	}

	/**
	 * Applies visibility settings to all elements within the shadow DOM based content.
	 * This ensures elements are shown or hidden according to the presence of variable data
	 * and the component settings file.
	 */
	protected conditionalVisibility(): void {
		WebComponent_Utils.applyVisibilitySettings(<ShadowRoot>this.shadowRoot);
	}

	/**
	 * Attaches listeners
	 * @private
	 * @returns {void} This method does not return a value.
	 */
	protected attachListeners(): void {
		// GTM Click Events
		WebComponent_Utils.addGTMClickListener(
			this.shadowRoot!,
			'.wc-content__cta',
			'wc-content',
			'cta link',
		);

		// Handle 'Add to basket' form submission
		const form = this.shadowRoot!.querySelector('.wc-content__atb-form') as HTMLFormElement;
		const productIdInput = this.shadowRoot!.querySelector(
			'.wc-content__product-id',
		) as HTMLInputElement;
		const productTypeInput = this.shadowRoot!.querySelector(
			'.wc-content__product-type',
		) as HTMLInputElement;

		if (form) {
			form.addEventListener('submit', (event: Event) => {
				//console.log('submit');
				event.preventDefault(); // Prevent the default form submission behavior
				const productId =
					productIdInput instanceof HTMLInputElement
						? parseInt(productIdInput.value)
						: NaN;

				if (!Number.isInteger(productId)) {
					NotificationService.showNotification(
						NotificationType.error,
						'Add to Basket Failed',
						'Sorry, we could not add this item to your basket',
					);
					return;
				}

				// Retrieve the input values
				// Ensure that productType is a string by checking if productTypeInput is an input element
				const productType =
					productTypeInput instanceof HTMLInputElement
						? productTypeInput.value
						: 'default';
				// Custom form handling logic, like adding to cart or updating UI
				handleFormSubmission(this, productId, productType, '1', false);
			});
		}
	}
}

/**
 * Registers the custom element:
 * - Checks if the component has already been defined to avoid registration errors
 * - Registers the component with the browser’s CustomElementRegistry
 * - Automatically re-registers the component if the page is restored from Safari’s back-forward cache (bfcache)
 */
WebComponent_Utils.defineComponent('wc-content', WcContent);
